import {AsyncSubject, defer, from, of, throwError} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

const RxResponse = function (promiseResponse) {
  this.status = promiseResponse.status;
  this.ok = promiseResponse.ok;
  this.statusText = promiseResponse.statusText;
  this.url = promiseResponse.url;
  this.headers = promiseResponse.headers;
  this._observableText = defer(() => from(promiseResponse.text()));
  this._observableBlob = defer(() => from(promiseResponse.blob()));
  return this;
};

RxResponse.prototype.text = function () {
  return this._observableText;
};

RxResponse.prototype.json = function () {
  return this.text().pipe(map(JSON.parse));
};

RxResponse.prototype.blob = function () {
  return this._observableBlob;
};

const rxFetch = (url, options) => {
  // At first glance, Rx.Observable.fromPromise would seem like the correct way
  // to implement this, *but* calling fetch(...) causes work to start on the
  // call right away, making this effectively a hot observable. In order to
  // preserve the semantics of this being a cold observable, we defer invocation
  // until subscription time.

  // See https://github.com/Reactive-Extensions/RxJS/blob/master/doc/gettingstarted/creating.md#cold-vs-hot-observables
  // for a definition of the "hot" and "cold" terms.

  let didSubscribe = false;

  let result = defer(() => {
    if (didSubscribe) {
      throw new Error('can not subscribe to rxjs-fetch result more than once');
    }
    didSubscribe = true;

    const subject = new AsyncSubject();

    fetch(url, options)
      .then(promiseResponse => {
        subject.next(new RxResponse(promiseResponse));
        subject.complete();
      })
      .catch(err => subject.error(err));

    return subject;
  });

  const throwHttpError = response => {
    let err = new Error('HTTP Error ' + response.status + ' on ' + url + ': ' + response.statusText);
    err.response = response;
    return throwError(err);
  };

  result.failOnHttpError = () => {
    return result.pipe(switchMap(response => {
      if (!response.ok) {
        return throwHttpError(response);
      } else {
        return of(response);
      }
    }));
  };

  result.text = () => {
    return result.failOnHttpError()
      .pipe(switchMap(response => response.text()));
  };

  result.json = () => {
    return result.failOnHttpError()
      .pipe(switchMap(response => response.json()));
  };

  result.blob = () => {
    return result.failOnHttpError()
      .pipe(switchMap(response => {
        const contentDisposition = response.headers.get('Content-Disposition');
        let type = undefined;
        let filename = undefined;

        if (contentDisposition) {
          const match = contentDisposition.match(/^(\w+); .*filename="([^"]+)"/);

          if (match) {
            type = match[1];
            filename = match[2];
          }
        }

        return response.blob().pipe(map(blob => {
          return {
            type: type,
            filename: filename,
            blob: blob
          };
        }));
      }));
  };

  return result;
};

export default rxFetch;



// WEBPACK FOOTER //
// ./modules/issFrontend/utils/rxFetch.js